import * as K from "../../lib/const.js";
import * as api from "../../lib/api.js";
import * as audio from "../../lib/audio.js";
import * as ecapa from "../../lib/ecapa.js";
import * as sign from "../../lib/sign.js";
import * as store from "../../lib/store.js";
import { t } from "../contract/strings";
import { linkThisDevice } from "./signin";
import { loadNormalisedCredentials } from "./credentials";
import { useApp } from "../state/store";

/**
 * What to read for each take.
 *
 * Harvard sentences (IEEE Recommended Practice for Speech Quality Measurements), two per
 * take. Borrowed rather than written because those lists are built to cover a spread of
 * English sounds instead of to read well, which is the property wanted here.
 *
 * Not claimed: that these three *are* a phonetically balanced set. Balance in those lists
 * is a property of a full list of ten, and these pairs are drawn across lists. Whether the
 * coverage they do have beats three sentences somebody improvises is untested — the reason
 * for prompting at all is the failure below, not the phonetics.
 *
 * They are also, deliberately, not memorable. A line you recite from memory comes out flat;
 * a line you have to read comes out at an ordinary speaking pace.
 *
 * **Length is a hard constraint, not a preference.** `ENROLL_TAKE_SEC` is 7.0 and the slice
 * is taken at exactly that, so anything past it is cut mid-word — an app visibly failing
 * its own instruction. 14 words is ~6.5 s at the slow, careful 130 wpm people read at when
 * a clock is running and a microphone is on. Do not lengthen these without moving
 * ENROLL_TAKE_SEC, and that is a mirrored constant (see const.js).
 */
export const TAKE_SCRIPTS = [t("enroll.script_1"), t("enroll.script_2"), t("enroll.script_3")];

/** The countdown slot at rest: what the card is asking for, not how long is left. */
export const TAKE_COUNT_IDLE = t("enroll.read_aloud");

/** Below this the takes are not describing one voice. See `recordTake`. */
const AGREEMENT_FLOOR = 0.45;

export interface Take {
  vector: number[];
  seconds: number;
  capturedAt: string;
}

export interface TakeResult {
  take?: Take;
  status: { message: string; kind: "ok" | "warn" | "err" | "" };
}

/**
 * What a running take reports while it runs. The countdown is a whole number of seconds
 * and changes seven times in a take; the level is read every frame by the meter itself.
 */
export interface TakeHooks {
  onCountIn: (secondsLeft: number | null) => void;
  onCountdown: (secondsLeft: number) => void;
  onStatus: (message: string, kind: "ok" | "warn" | "err" | "") => void;
  /** Registers the peak source with the meter, and unregisters it when the mic closes. */
  onLive: (read: (() => number) | null) => void;
}

/**
 * One take: open the microphone, count in, capture `ENROLL_TAKE_SEC`, embed what came back.
 *
 * The timer resolves and the frame loop only paints, and that split matters:
 * `requestAnimationFrame` stops firing on a backgrounded tab, so driving the stop off it
 * would leave the microphone open until the person came back, and then embed however many
 * minutes of audio that produced. Here the stop is a `setTimeout` and the meter is the
 * component's own rAF; this function owns neither.
 *
 * `endLive` runs the moment the microphone closes, *not* only from the `finally` --
 * `ecapa.embed` sits between the two, and for the length of that pass the card would
 * otherwise still be showing an accent border, a live meter and a countdown at zero for a
 * microphone that is shut. It is idempotent, so `finally` is still the error path's net.
 */
export async function recordTake(existing: Take[], hooks: TakeHooks): Promise<TakeResult> {
  const recorder = new audio.Recorder();
  let ended = false;
  const endLive = () => {
    if (ended) return;
    ended = true;
    hooks.onLive(null);
    hooks.onCountIn(null);
    hooks.onCountdown(0);
  };

  try {
    const caps = await recorder.start();
    const warnings = audio.captureWarnings(caps);

    // Open the microphone first so a permission prompt cannot arrive after the visible
    // count-in. The three seconds are excluded from the sample below; speech starts only
    // when the overlay clears and the seven-second take begins.
    await new Promise<void>((resolve) => {
      let left = 3;
      hooks.onCountIn(left);
      const timer = setInterval(() => {
        left -= 1;
        if (left > 0) {
          hooks.onCountIn(left);
        } else {
          clearInterval(timer);
          hooks.onCountIn(null);
          resolve();
        }
      }, 1000);
    });

    const recordingFrom = recorder.seconds;
    recorder.takePeak();
    hooks.onStatus(
      warnings.length
        ? t("enroll.status_warning", { warning: warnings[0] })
        : t("enroll.status_listening_line"),
      warnings.length ? "warn" : ""
    );
    hooks.onLive(() => recorder.takePeak());

    await new Promise<void>((resolve) => {
      const startedAt = performance.now();
      // Whole seconds only. The value on screen changes seven times in a take, so this
      // ticks rather than riding the meter's frame loop and re-rendering the card 60 times
      // a second to write the same digit.
      const tick = setInterval(() => {
        const left = K.ENROLL_TAKE_SEC - (performance.now() - startedAt) / 1000;
        hooks.onCountdown(Math.max(0, Math.ceil(left)));
      }, 200);
      hooks.onCountdown(Math.ceil(K.ENROLL_TAKE_SEC));
      setTimeout(() => {
        clearInterval(tick);
        resolve();
      }, K.ENROLL_TAKE_SEC * 1000);
    });

    const wav = recorder.slice(recordingFrom, recorder.seconds);
    await recorder.stop();
    endLive();

    const trimmed = audio.trimSilence(wav);
    const seconds = trimmed.length / K.SAMPLE_RATE;
    if (seconds < K.MIN_SPEECH_SEC) {
      // Below three seconds the scores are noise, so this raises rather than quietly
      // enrolling something useless.
      return {
        status: {
          message: t("enroll.status_short", {
            seconds: seconds.toFixed(1),
            min: K.MIN_SPEECH_SEC,
          }),
          kind: "err",
        },
      };
    }

    // One ECAPA pass on a device is seconds, not milliseconds, and the button is disabled
    // throughout it. Named, or the gap between "stopped" and "captured" reads as a hang.
    hooks.onStatus(t("enroll.status_checking_take"), "");
    const vector = await ecapa.embed(trimmed);
    const take: Take = {
      vector: Array.from(vector),
      seconds,
      capturedAt: new Date().toISOString(),
    };

    const all = [...existing, take];
    // `ecapa.cosine` handed over directly, as `app.js` does: `takeAgreement` passes the
    // vectors through untouched, so there is nothing to convert and nothing to get wrong.
    const agreement = store.takeAgreement(all, ecapa.cosine);
    if (agreement !== null && agreement < AGREEMENT_FLOOR) {
      return {
        take,
        status: {
          message: t("enroll.status_different"),
          kind: "warn",
        },
      };
    }
    return {
      take,
      status: {
        message: t("enroll.status_captured", { count: all.length, seconds: seconds.toFixed(1) }),
        kind: "ok",
      },
    };
  } catch (err) {
    await recorder.stop().catch(() => {});
    return { status: { message: t("enroll.status_mic_problem", { message: (err as Error).message }), kind: "err" } };
  } finally {
    endLive();
  }
}

/**
 * Turn the takes into this device's identity: a template, a signing key, a registration.
 *
 * Returns a warning to show on the *next* screen when the account link failed, because by
 * then this one is gone.
 */
export async function finishEnrollment(name: string, takes: Take[]): Promise<string | null> {
  const app = useApp.getState();

  const keypair = await sign.generateKeypair();
  const publicKey = await sign.publicKeyBase64(keypair);
  const centroid = ecapa.centroid(takes.map((t) => Float32Array.from(t.vector)));
  const caps = await probeCapabilitiesQuietly();

  const claimProfileId = `dev_${crypto.randomUUID().replace(/-/g, "").slice(0, 24)}`;
  const registered = await api.registerDevice({
    claim_profile_id: claimProfileId,
    public_key: publicKey,
    display_name: name,
    capabilities: caps,
  });

  api.setToken(registered.token);
  const credentials = {
    token: registered.token,
    claim_profile_id: registered.claim_profile_id,
    claimProfileId: registered.claim_profile_id,
    participantId: registered.participant_id,
    displayName: registered.display_name,
    // Starts the rotation clock (#436); see `token-rotation.ts`.
    tokenIssuedAt: Date.now(),
  };
  const template = store.makeTemplate(
    takes,
    Array.from(centroid),
    `${navigator.platform || "browser"} @ ${caps.sample_rate ?? "?"}Hz`
  );

  await store.saveKeypair(keypair);
  await store.saveTemplate(template);
  await store.saveCredentials(credentials);
  app.setIdentity({ credentials, template, keypair });

  // Link now if they signed in *before* setting up, which is the order the setup screen
  // invites for anyone starting a session.
  //
  // [V] Without this the two halves never met: `linkThisDevice` returns early when there
  // are no device credentials, so signing in on a fresh device linked nothing, and enrolment
  // afterwards registered a device that no account owned. Tapping Create then answered
  // "sign in on this device to start a session" -- to somebody who was signed in, on that
  // device.
  return await linkThisDevice(undefined, "enrollment");
}

/**
 * Replace this device's voice template without touching its identity.
 *
 * The counterpart to :func:`finishEnrollment` for re-recording: the signing key, device id
 * and credentials are all kept, so no new device row is created on the server and every
 * signature already on file stays verifiable. Only the local voice template is overwritten.
 * That is the Option A fix for the duplicate-row problem: re-recording your voice should not
 * cost you your history (issue #9).
 */
export async function rerecordVoice(takes: Take[]): Promise<void> {
  const app = useApp.getState();
  const [keypair, credentials] = await Promise.all([
    store.loadKeypair(),
    loadNormalisedCredentials(),
  ]);
  if (!keypair || !credentials) {
    throw new Error(t("enroll.error_no_identity"));
  }
  const centroid = ecapa.centroid(takes.map((t) => Float32Array.from(t.vector)));
  const caps = await probeCapabilitiesQuietly();
  const template = store.makeTemplate(
    takes,
    Array.from(centroid),
    `${navigator.platform || "browser"} @ ${caps.sample_rate ?? "?"}Hz`
  );
  await store.saveTemplate(template);
  app.setIdentity({ credentials, template, keypair });
}

/**
 * What the microphone reports about itself, without asking the person for anything.
 *
 * Swallows its failures: this runs during setup to record capture conditions on the
 * device row, and a browser that refuses here has already refused the takes.
 */
export async function probeCapabilitiesQuietly() {
  try {
    const stream = await audio.openMic();
    const ctx = new AudioContext({ sampleRate: K.SAMPLE_RATE });
    const caps = audio.probeCapabilities(stream, ctx);
    stream.getTracks().forEach((t) => t.stop());
    await ctx.close();
    return caps;
  } catch {
    return {} as ReturnType<typeof audio.probeCapabilities>;
  }
}
